import { formatPrice, priorityBadge, safeHttpUrl } from '../utils/wishDisplay';
import SurpriseBadge from './SurpriseBadge';
import WishShareSection from './WishShareSection';

/**
 * A wish card with the owner/gifter split.
 *
 * OWNER-BLINDNESS (defense in depth, on top of the API): when the signed-in user
 * OWNS the wish we render no claim/purchase state and no gifting buttons. The
 * guard is derived from `wish.user_id` — the wish itself — rather than from
 * whatever the surrounding screen thinks it's showing, so a screen that gets its
 * own context wrong still can't leak.
 *
 * This branch is security-relevant, which is exactly why it lives in one file.
 * `ownerLabel` lets a screen word the owner badge for its context (e.g. the pet
 * profile says "Your pet's wish"); it never changes which branch is taken.
 *
 * `forceOwnerView` is an ADDITIONAL, screen-specific reason to treat the viewer
 * as the owner — it is OR-ed in, so it can only ever hide gifting UI, never
 * reveal it. The pet profile passes `isCaretaker` here: a caretaker and the
 * wish's `user_id` are the same person by an API invariant today, but if that
 * ever drifted we would still rather show a caretaker too little than too much.
 */
export default function WishGiftCard({
    wish,
    user,
    ownerLabel = 'Your wish',
    ownerActions = null,
    forceOwnerView = false,
    onClaim,
    onUnclaim,
    onPurchase,
    onUndoPurchase,
    busy = false,
    notify,
    onShareChanged,
    children,
}) {
    const price = formatPrice(wish.price_min, wish.price_max);

    // A wish FOR A CHILD is never treated as the viewer's own, even when the viewer is
    // the carer who added it (`wish.user_id`).
    //
    // This is the ONE place the owner branch can be opened rather than closed, and it is
    // deliberate. A child has no account, so there is no owner to be blind on behalf of,
    // and the carers are exactly the people who must see what has been bought — two
    // adults adding to one list otherwise buy the same present. The API already sends
    // full claim/purchase state for a child's wish (Wish::isHiddenFromViewer exempts
    // them); this stops the card hiding data the server chose to send.
    //
    // It cannot leak anything the API withheld: the card renders fields, it does not
    // invent them. If the server ever stopped sending claim state for child wishes, the
    // gifter branch would simply render nothing.
    const isForChild = Boolean(wish.child_id || wish.child);
    const isMine = !isForChild && (wish.user_id === user?.id || forceOwnerView);

    // A shareable ("chip in") wish — the gifter branch renders the pledge /
    // coordinator UI. The wisher never gets here (owner branch), and the API
    // never sends them the contributions anyway.
    const isShareable =
        wish.share_count != null || wish.estimated_price != null || !!wish.allow_partial_funding;

    const purchases = wish.purchases || [];
    const iPurchased =
        wish.purchase?.purchased_by_user_id === user?.id ||
        purchases.some((p) => p.purchased_by_user_id === user?.id);
    const anyPurchase =
        purchases.length > 0 || !!wish.purchase || wish.status === 'purchased' || wish.status === 'revealed';
    const iClaimed = wish.claimed_by_user_id && wish.claimed_by_user_id === user?.id;
    const claimedByOther = wish.claimed_by_user_id && !iClaimed;

    return (
        <div className="bg-white rounded-lg shadow p-4 flex flex-col">
            {wish.image_url && (
                <img src={wish.image_url} alt="" className="w-full h-32 object-cover rounded-md mb-3" />
            )}
            <div className="flex items-start justify-between gap-2">
                <h3 className="font-semibold text-gray-900">{wish.title}</h3>
                {wish.priority && (
                    <span className={`text-xs px-2 py-0.5 rounded ${priorityBadge(wish.priority)}`}>
                        {wish.priority}
                    </span>
                )}
            </div>

            {wish.suggested_by_user_id && <SurpriseBadge wish={wish} className="mt-2" />}

            {children}

            {wish.description && <p className="text-sm text-gray-600 mt-2">{wish.description}</p>}

            <div className="mt-2 space-y-1 text-sm">
                {price && <p className="text-gray-700">{price}</p>}
                {wish.quantity_desired > 1 && <p className="text-gray-500 text-xs">Wants {wish.quantity_desired}</p>}
                {wish.category && <p className="text-gray-500 text-xs">{wish.category.icon} {wish.category.name}</p>}
                <div className="flex flex-wrap gap-1">
                    {wish.type === 'charity' && (
                        <span className="text-xs px-2 py-0.5 rounded bg-pink-100 text-pink-700">
                            💝 Charity{wish.organization_name ? ` · ${wish.organization_name}` : ''}
                        </span>
                    )}
                    {wish.multiple_ok && <span className="text-xs px-2 py-0.5 rounded bg-purple-100 text-purple-700">Multiple OK</span>}
                    {wish.is_generic && <span className="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-600">Idea</span>}
                    {isShareable && <span className="text-xs px-2 py-0.5 rounded bg-teal-100 text-teal-700">Group gift</span>}
                    {wish.event && <span className="text-xs px-2 py-0.5 rounded bg-indigo-100 text-indigo-700">🎉 {wish.event.name}</span>}
                </div>
                {safeHttpUrl(wish.url) && (
                    <a href={safeHttpUrl(wish.url)} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline text-xs inline-block">
                        View product ↗
                    </a>
                )}
            </div>

            <div className="mt-auto pt-3">
                {isMine ? (
                    // OWNER VIEW — never any claim/purchase state or buttons.
                    <div className="flex gap-2 items-center">
                        <span className="text-xs px-2 py-1 rounded bg-blue-50 text-blue-600 mr-auto">{ownerLabel}</span>
                        {ownerActions}
                    </div>
                ) : (
                    // GIFTER VIEW — claim / purchase controls + honest state.
                    <div className="space-y-2">
                        <GiftStatus
                            anyPurchase={anyPurchase}
                            multipleOk={wish.multiple_ok}
                            purchaseCount={purchases.length}
                            iPurchased={iPurchased}
                            iClaimed={iClaimed}
                            claimedByOther={claimedByOther}
                            claimerName={wish.claimed_by?.name}
                            status={wish.status}
                        />
                        {isShareable && (
                            <WishShareSection
                                wish={wish}
                                user={user}
                                busy={busy}
                                notify={notify}
                                onChanged={onShareChanged}
                            />
                        )}
                        <div className="flex flex-wrap gap-2">
                            {(!anyPurchase || wish.multiple_ok) && !claimedByOther && !iClaimed && (
                                <button disabled={busy} onClick={onClaim} className="text-sm px-3 py-1 border border-amber-300 text-amber-700 rounded hover:bg-amber-50 disabled:opacity-50">
                                    Claim
                                </button>
                            )}
                            {iClaimed && (
                                <button disabled={busy} onClick={onUnclaim} className="text-sm px-3 py-1 border border-amber-300 text-amber-700 rounded hover:bg-amber-50 disabled:opacity-50">
                                    Release claim
                                </button>
                            )}
                            {(!anyPurchase || wish.multiple_ok) && (
                                <button disabled={busy} onClick={onPurchase} className="text-sm px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50">
                                    Purchase
                                </button>
                            )}
                            {iPurchased && (
                                <button disabled={busy} onClick={onUndoPurchase} className="text-sm px-3 py-1 border border-gray-300 text-gray-600 rounded hover:bg-gray-50 disabled:opacity-50">
                                    Undo my purchase
                                </button>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export function GiftStatus({ anyPurchase, multipleOk, purchaseCount, iPurchased, iClaimed, claimedByOther, claimerName, status }) {
    if (status === 'revealed') {
        return <span className="text-xs px-2 py-1 rounded bg-green-100 text-green-700">Revealed — purchased</span>;
    }
    if (anyPurchase && !multipleOk) {
        return <span className="text-xs px-2 py-1 rounded bg-green-100 text-green-700">{iPurchased ? 'Purchased by you' : 'Purchased'}</span>;
    }
    if (multipleOk && purchaseCount > 0) {
        return <span className="text-xs px-2 py-1 rounded bg-green-50 text-green-700">Purchased ×{purchaseCount} — still available</span>;
    }
    if (iClaimed) {
        return <span className="text-xs px-2 py-1 rounded bg-amber-100 text-amber-700">You claimed this</span>;
    }
    if (claimedByOther) {
        return <span className="text-xs px-2 py-1 rounded bg-amber-100 text-amber-700">Claimed{claimerName ? ` by ${claimerName}` : ''}</span>;
    }
    return <span className="text-xs px-2 py-1 rounded bg-blue-50 text-blue-600">Available</span>;
}
